/**
 * Survey line numbering (Zdas / Geosoft / TJR convention).
 *
 * A production line number is `B S NNN V`: block digit, sub-digit (9 = tie line), sequence, version digit
 * (0 = original, 1–9 = reflight). Zdas logs write `<line>.<version>` (e.g. `10001.0`), Geosoft names the
 * line `L100010`. Numbers below 10000 are not production: 5010/5020 ground checks, 7010/7020 comp box,
 * 8010/8020 test lines (pre-/post-day), still with a trailing version digit.
 *
 * Source: PROJECT_FOLDER_STRUCTURE.md, TJF_IMPORT_EXPORT_SPEC.md §3.3, report/make_weekly.py (Flight).
 */

export type LineKind = 'traverse' | 'tie' | 'ground_check' | 'comp_box' | 'test' | 'other';

export interface ParsedLine {
  /** The full number as flown, e.g. 100010 or 7010. */
  readonly number: number;
  /** The number with the version digit zeroed, e.g. 100010 for 100017; used to look up the planned line. */
  readonly base: number;
  /** 0 for the original flight, 1–9 for reflights. */
  readonly version: number;
  /** First digit of a production line (the block), or null for non-production numbers. */
  readonly blockDigit: string | null;
  readonly kind: LineKind;
  /** True for traverse and tie lines, which count towards line km. */
  readonly production: boolean;
}

const NON_PRODUCTION_KINDS: Readonly<Record<number, LineKind>> = {
  501: 'ground_check',
  502: 'ground_check',
  701: 'comp_box',
  702: 'comp_box',
  801: 'test',
  802: 'test',
};

/** Parse a flown line number (as in a Geosoft line name without the `L`). */
export function parseLineNumber(number: number): ParsedLine {
  if (!Number.isInteger(number) || number < 0) {
    throw new RangeError(`line number must be a non-negative integer, got ${number}`);
  }
  const version = number % 10;
  const base = number - version;
  if (number < 10000) {
    const kind = NON_PRODUCTION_KINDS[Math.trunc(number / 10)] ?? 'other';
    return { number, base, version, blockDigit: null, kind, production: false };
  }
  const digits = String(number);
  const blockDigit = digits[0] ?? null;
  const kind: LineKind = digits[1] === '9' ? 'tie' : 'traverse';
  return { number, base, version, blockDigit, kind, production: true };
}

/** Convert the Zdas log form `<line>.<version>` (two strings as logged) to the full line number. */
export function zdasToLineNumber(line: string, version: string): number {
  const l = Number.parseInt(line, 10);
  const v = Number.parseInt(version, 10);
  if (!Number.isInteger(l) || !Number.isInteger(v) || v < 0 || v > 9) {
    throw new RangeError(`invalid Zdas line "${line}.${version}"`);
  }
  return l * 10 + v;
}

/**
 * Geosoft line name as the weekly generator and Total.gdb write it: `L100010`, a reflight with its version digit
 * (`L100251`), and `T` for a tie line (second digit 9: `T190310`).
 */
export function geosoftLineName(number: number): string {
  const s = String(number);
  return `${s.length >= 5 && s[1] === '9' ? 'T' : 'L'}${s}`;
}
